import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import SearchIcon from '@/assets/icons/search.svg?react';
import {
  useGetMyReceivers,
  useSendPartnershipRequest,
} from '@/hooks/useReceivers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { PAGE_SIZE } from '@/constants/pageSize';
import { LabelValue } from '@/types/global';
import { Control, useForm, useWatch } from 'react-hook-form';
import { Dispatch, SetStateAction, useState } from 'react';
import Loader from '@/components/ui/Loader';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledInput from '@/components/styled/StyledInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StyledTable from '@/components/styled/StyledTable';
import { RECEIVER_COLUMNS } from '@/types/receivers/ReceiverColumnDef';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import { ReceiversResponse } from '@/api/receiver.api';
import { RowSelectionState, SortingState } from '@tanstack/react-table';
import StyledListHeader from '@/components/styled/StyledListHeader';
import PartnershipRequestsList from '@/components/receivers/PartnershipRequestsList';
import AddReceiversPage from './receivers/AddReceiversPage';
import PartnershipSentConfirmationModal from '@/components/receivers/PartnershipSentConfirmationModal';
import PartnershipRequestModal from '@/components/receivers/PartnershipRequestModal';
import PlusIcon from '@/assets/icons/plus.svg?react';
import ReceiverInfoModal from '@/components/receivers/ReceiverInfoModal';
import RemoveReceiverModal from '@/components/receivers/RemoveReceiverModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface ReceiverFilters {
  search: string;
  country?: LabelValue<string>;
}

type tabOptions =
  | 'myReceivers'
  | 'currentRequests'
  | 'recentReceivers'
  | 'otherReceivers';

const ReceiversPage = () => {
  const { t } = useTranslation();

  const { control } = useForm<ReceiverFilters>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      country: undefined,
    },
  });

  const searchValue = useWatch({ control, name: 'search' });
  const debouncedSearch = useDebouncedSearch(searchValue);
  const countryValue = useWatch({ control, name: 'country' });

  const filters = {
    name: debouncedSearch,
    country: countryValue?.value ?? '',
  };

  const { page, setPage, setSorting, sorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: receiversListResponse,
    isSuccess,
    isLoading,
  } = useGetMyReceivers(
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
    },
    (data) => ({
      ...data,
      countries: data.countries.map((country) => ({
        label: country,
        value: country,
      })),
    }),
  );

  const [currTab, setCurrTab] = useState<tabOptions>('myReceivers');
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [
    receiversToSendPartnershipRequest,
    setReceiversToSendPartnershipRequest,
  ] = useState<string[]>([]);

  const { mutateAsync: sendPartnershipRequest } = useSendPartnershipRequest();

  const selectedReceiverIds = Object.keys(selection);

  const [
    isPartnershipSentConfirmationModalOpen,
    setIsPartnershipSentConfirmationModalOpen,
  ] = useState(false);

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('pages.dashboard.metrics.receivers')}
        subTitle={t('pages.receivers.subTitle')}
      >
        {currTab === 'myReceivers' || currTab === 'currentRequests' ? (
          <StyledButton
            title={t('pages.receivers.addNewReceivers')}
            className="px-3 py-2"
            onClick={() => {
              setCurrTab('otherReceivers');
            }}
            icon={<SearchIcon className="size-5 stroke-white" />}
          />
        ) : (
          <StyledButton
            title={t('pages.receivers.addSelectedReceivers')}
            className="px-3 py-2"
            onClick={() => {
              setReceiversToSendPartnershipRequest(selectedReceiverIds);
            }}
            disabled={selectedReceiverIds.length === 0}
            icon={<PlusIcon className="size-5 fill-white" />}
          />
        )}
      </StyledListHeader>
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess && (
          <div className="flex flex-col w-full h-auto p-6 gap-6">
            <Tabs
              defaultValue="myReceivers"
              value={currTab}
              className="w-full"
              onValueChange={(value) => {
                setCurrTab(value as tabOptions);
                setSelection({});
              }}
            >
              <div className="flex w-full justify-between">
                <TabsList>
                  <TabsTrigger
                    value="myReceivers"
                    className="hover:cursor-pointer"
                  >
                    {t('pages.receivers.myReceivers')}
                  </TabsTrigger>
                  {receiversListResponse.countRequests > 0 && (
                    <TabsTrigger
                      value="currentRequests"
                      className="hover:cursor-pointer flex items-center justify-center gap-2"
                    >
                      <span>{t('pages.receivers.currentRequests')}</span>
                      <div className="size-5 flex justify-center items-center text-warning-100 bg-warning-500 border border-warning-600 rounded-2xl px-1.5 py-0.5">
                        {receiversListResponse.countRequests}
                      </div>
                    </TabsTrigger>
                  )}
                  {receiversListResponse.countRecent > 0 && (
                    <TabsTrigger
                      value="recentReceivers"
                      className="hover:cursor-pointer flex items-center justify-center gap-2"
                    >
                      <span>{t('pages.receivers.newReceivers')}</span>
                      <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                        {receiversListResponse.countRecent}
                      </div>
                    </TabsTrigger>
                  )}
                  <TabsTrigger
                    value="otherReceivers"
                    className="hover:cursor-pointer"
                  >
                    {t('pages.receivers.otherReceivers')}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="myReceivers">
                {isSuccess && (
                  <ReceiversList
                    receiversListResponse={receiversListResponse}
                    page={page}
                    setPage={setPage}
                    sorting={sorting}
                    setSorting={setSorting}
                    control={control}
                  />
                )}
              </TabsContent>
              <TabsContent value="currentRequests">
                <PartnershipRequestsList />
              </TabsContent>
              <TabsContent value="recentReceivers">
                <AddReceiversPage
                  onlyRecent={true}
                  setSelection={setSelection}
                  setReceiversToSendPartnershipRequest={
                    setReceiversToSendPartnershipRequest
                  }
                />
              </TabsContent>
              <TabsContent value="otherReceivers">
                <AddReceiversPage
                  onlyRecent={false}
                  setSelection={setSelection}
                  setReceiversToSendPartnershipRequest={
                    setReceiversToSendPartnershipRequest
                  }
                />
              </TabsContent>
            </Tabs>
          </div>
        )
      )}
      {receiversToSendPartnershipRequest.length > 0 && (
        <PartnershipRequestModal
          closeModal={() => setReceiversToSendPartnershipRequest([])}
          onConfirm={async () => {
            await sendPartnershipRequest(receiversToSendPartnershipRequest);
            setIsPartnershipSentConfirmationModalOpen(true);
          }}
          multiple={receiversToSendPartnershipRequest.length > 1}
        />
      )}
      {isPartnershipSentConfirmationModalOpen && (
        <PartnershipSentConfirmationModal
          closeModal={() => setIsPartnershipSentConfirmationModalOpen(false)}
        />
      )}
    </StyledContainer>
  );
};

export default ReceiversPage;

const ReceiversList = ({
  receiversListResponse,
  page,
  setPage,
  sorting,
  setSorting,
  control,
}: {
  receiversListResponse: Omit<ReceiversResponse, 'countries'> & {
    countries: LabelValue<string>[];
  };
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  control: Control<ReceiverFilters>;
}) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [receiverToViewId, setReceiverToViewId] = useState<string | null>(null);
  const [receiverToDeleteId, setReceiverToDeleteId] = useState<string | null>(
    null,
  );

  return (
    <div className="flex flex-col gap-6 w-full h-auto pt-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-normal text-gray-600">
          {t('pages.receivers.filterBy')}
        </span>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={receiversListResponse.countries}
              name="country"
              placeholder={t('pages.receivers.country')}
              isClearable={true}
            />
          </div>
          <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
            <StyledInput
              control={control}
              name="search"
              placeholder={t('pages.receivers.quickSearch')}
              icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
            />
          </div>
        </div>
      </div>
      <StyledTable
        columns={getTranslatedConst(RECEIVER_COLUMNS)}
        data={receiversListResponse.receivers}
        sorting={sorting}
        setSorting={setSorting}
        getRowId={(row) => row.id}
        onViewRow={(row) => {
          setReceiverToViewId(row.original.id);
        }}
        onDeleteRow={(row) => {
          setReceiverToDeleteId(row.original.id);
        }}
      />
      <StyledNextPreviousButtons
        page={page}
        pageSize={PAGE_SIZE}
        setPage={setPage}
        dataLength={receiversListResponse.total}
        label="fournisseur"
      />
      {receiverToViewId && (
        <ReceiverInfoModal
          receiverId={receiverToViewId}
          closeModal={() => setReceiverToViewId(null)}
        />
      )}
      {receiverToDeleteId && (
        <RemoveReceiverModal
          receiverId={receiverToDeleteId}
          closeModal={() => setReceiverToDeleteId(null)}
        />
      )}
    </div>
  );
};
